import React, { useEffect, useState } from 'react';
import { useI18n } from '../../context/i18n';
import { playsTranslations } from './playsCopy';
import { DISPLAY_PLANS, mergePublicPlans, PACK_PRICE_MONTHLY } from './plansTable';
import { fetchPublicPlans, selectPlan, cancelSubscription, PlaysApiError } from './playsApi';
import './PlaysPricingPage.css';

const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
    <path d="M2.5 7l3 3 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const CONTACT_MAILTO = 'mailto:hello@upsellerate.co?subject=Multiple%20stores';

// Render a footnote string making the trailing "Talk to us." a contact link
// (same mailto as the multi-store block). Falls back to plain text if the
// phrase is not present in the backend-provided string.
const FootnoteWithContact = ({ text }) => {
  const marker = 'Talk to us.';
  const idx = (text || '').lastIndexOf(marker);
  if (idx === -1) return <>{text}</>;
  return (
    <>
      {text.slice(0, idx)}
      <a href={CONTACT_MAILTO} className="plays-pricing-multistore-link">{marker}</a>
      {text.slice(idx + marker.length)}
    </>
  );
};

const DashIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
    <path d="M3 7h8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

// Pricing page for the "Revenue Plays" commercial model. 3 columns
// (Free / Core / Pro), headline block + always-on explainer + Klaviyo
// objection copy (verbatim, PLAYS_PRICING_SPEC.md section 7), Play Packs
// visible on Core/Pro, and a founding disclosure banner shown only when the
// account (or the public catalog) flags founding availability.
//
// Replaces the old tiers-by-slots PricingPage (Free/Starter/Mid/Growth,
// billing/plans.js) which is being retired in this same change.
export default function PlaysPricingPage({ token, currentPlanTier, currentPlanPacks = 0, isFounding, onSubscribed }) {
  const { language } = useI18n();
  const copy = playsTranslations[language]?.pricing || playsTranslations.en.pricing;
  const foundingCopy = playsTranslations[language]?.founding || playsTranslations.en.founding;

  const [plans, setPlans] = useState(DISPLAY_PLANS);
  const [foundingDisclosure, setFoundingDisclosure] = useState(foundingCopy.disclosure);
  const [pricingFootnote, setPricingFootnote] = useState(copy.gmvFootnote);
  const [packsByPlan, setPacksByPlan] = useState({});
  const [pendingPlan, setPendingPlan] = useState(null);
  const [error, setError] = useState('');
  // High-GMV accounts get `pricing_footnote` back from select-plan: show it
  // prominently before redirecting to checkout (never blocks the checkout).
  const [gmvCheckoutNotice, setGmvCheckoutNotice] = useState(null); // {footnote, confirmationUrl}

  useEffect(() => {
    let cancelled = false;
    fetchPublicPlans()
      .then((data) => {
        if (cancelled) return;
        setPlans(mergePublicPlans(DISPLAY_PLANS, data?.plans));
        if (data?.founding_disclosure) setFoundingDisclosure(data.founding_disclosure);
        if (data?.pricing_footnote) setPricingFootnote(data.pricing_footnote);
      })
      .catch(() => {
        // Keep the static DISPLAY_PLANS fallback; pricing must never blank out.
      });
    return () => { cancelled = true; };
  }, []);

  // Seed the current paid plan's pack stepper with what the merchant already
  // has, so "add one more" is meaningful and the CTA can detect a real change.
  useEffect(() => {
    if (currentPlanTier && currentPlanTier !== 'free') {
      setPacksByPlan((prev) => ({ ...prev, [currentPlanTier]: currentPlanPacks }));
    }
  }, [currentPlanTier, currentPlanPacks]);

  const packsFor = (tier) => packsByPlan[tier] || 0;
  // Clamp at the per-plan pack cap (max_packs from /billing/plans). null =
  // unlimited (Accelerate); undefined/NaN means packs not allowed (cap 0).
  const setPacks = (tier, next, maxPacks) => {
    const cap = maxPacks === null ? Infinity : (Number.isFinite(maxPacks) ? maxPacks : 0);
    setPacksByPlan((prev) => ({ ...prev, [tier]: Math.min(cap, Math.max(0, next)) }));
  };

  const handleDowngradeToFree = async () => {
    setError('');
    setPendingPlan('free');
    try {
      await cancelSubscription({ token });
      // Reload so the whole view reflects the Free plan (meter, CTAs, etc.).
      window.location.reload();
    } catch (err) {
      const message = err instanceof PlaysApiError ? err.message : (err?.message || copy.genericError);
      setError(message);
      setPendingPlan(null);
    }
  };

  const handleSubscribe = async (plan) => {
    setError('');
    setPendingPlan(plan.tier);
    try {
      if (plan.tier === 'free') {
        throw new Error('Select a paid plan to launch Plays.');
      }
      const result = await selectPlan({ token, plan: plan.tier, playPacks: packsFor(plan.tier) });
      if (result?.confirmationUrl) {
        onSubscribed?.(result);
        // High-GMV band: show the footnote prominently before redirecting.
        // Informational only, never blocks: redirect proceeds on click.
        if (result.pricing_footnote) {
          setGmvCheckoutNotice({
            footnote: result.pricing_footnote,
            confirmationUrl: result.confirmationUrl,
          });
          setPendingPlan(null);
          return;
        }
        window.location.href = result.confirmationUrl;
        return;
      }
      throw new Error('Missing confirmation URL from server.');
    } catch (err) {
      // Structured pack-cap error: {error, code: 'pack_limit_reached',
      // meta: {max_packs}}. Clamp the stepper and show the backend message.
      if (err instanceof PlaysApiError && err.code === 'pack_limit_reached') {
        const maxPacks = err.meta?.max_packs;
        if (Number.isFinite(maxPacks)) {
          setPacksByPlan((prev) => ({ ...prev, [plan.tier]: Math.min(prev[plan.tier] || 0, maxPacks) }));
        }
      }
      const message = err instanceof PlaysApiError ? err.message : (err?.message || copy.genericError);
      setError(message);
      setPendingPlan(null);
    }
  };

  return (
    <div className="plays-pricing-page">
      <div className="plays-pricing-headline">
        <p className="plays-pricing-eyebrow">{copy.headlineEyebrow}</p>
        <h1 className="plays-pricing-title">{copy.headlineBody}</h1>
      </div>

      <div className="plays-pricing-explainer">{copy.alwaysOnExplainer}</div>

      {(isFounding || foundingDisclosure) && (
        <div className="plays-founding-banner" role="note">
          <span className="plays-founding-badge">{foundingCopy.badge}</span>
          <span>{foundingDisclosure}</span>
        </div>
      )}

      {error && <div className="plays-pricing-error">{error}</div>}

      <div className="plays-pricing-grid">
        {plans.map((plan) => {
          const isCurrent = currentPlanTier === plan.tier;
          const isPending = pendingPlan === plan.tier;
          const isFree = plan.tier === 'free';
          const isPro = plan.tier === 'pro';
          const packCount = packsFor(plan.tier);
          const packPrice = plan.packPriceMonthly ?? PACK_PRICE_MONTHLY;

          return (
            <div key={plan.tier} className={`plays-pricing-card ${isPro ? 'plays-pricing-card-featured' : ''}`}>
              {isPro && <span className="plays-pricing-badge">{copy.mostPopular}</span>}
              <h3 className="plays-pricing-card-name">{plan.name}</h3>

              {plan.tier === 'core' && <p className="plays-pricing-oneliner">{copy.coreOneLiner}</p>}
              {plan.tier === 'pro' && <p className="plays-pricing-oneliner">{copy.proOneLiner}</p>}

              <div className="plays-pricing-card-price">
                <span className="plays-pricing-card-amount">${plan.priceMonthly}</span>
                {plan.priceMonthly > 0 && <span className="plays-pricing-card-period">{copy.perMonth}</span>}
              </div>

              <ul className="plays-pricing-card-features">
                <li>
                  <CheckIcon />
                  <span>{plan.detectedPlaysCopy || copy.detectedPlays}</span>
                </li>
                <li>
                  <CheckIcon />
                  <span>{plan.chatCopy}</span>
                </li>
                <li>
                  {plan.oneTimeIncluded > 0 ? <CheckIcon /> : <DashIcon />}
                  <span>{copy.oneTimeIncluded(plan.oneTimeIncluded)}</span>
                </li>
                <li>
                  {plan.alwaysOnIncluded > 0 ? <CheckIcon /> : <DashIcon />}
                  <span>{copy.alwaysOnIncluded(plan.alwaysOnIncluded)}</span>
                </li>
                <li>
                  <CheckIcon />
                  <span>{copy.campaignsUnlimited}</span>
                </li>
                <li>
                  {plan.canActivate ? <CheckIcon /> : <DashIcon />}
                  <span>{plan.canActivate ? copy.exportAllowed : copy.exportBlocked}</span>
                </li>
                <li>
                  <CheckIcon />
                  <span>{plan.resultsHistory === 'full' ? copy.resultsHistoryFull : copy.resultsHistoryLimited}</span>
                </li>
                <li>
                  {plan.basicCalendar ? <CheckIcon /> : <DashIcon />}
                  <span>{copy.basicCalendar}</span>
                </li>
                <li>
                  <CheckIcon />
                  <span>{plan.onboarding === 'assisted' ? copy.assistedOnboarding : copy.selfServiceOnboarding}</span>
                </li>
                {plan.users != null && (
                  <li>
                    <CheckIcon />
                    <span>{plan.users === 'multiple' ? copy.multiUser : copy.singleUser}</span>
                  </li>
                )}
              </ul>

              {plan.packAvailable ? (
                <div className="plays-pricing-pack">
                  <div className="plays-pricing-pack-header">
                    <span>{copy.playPackLabel}</span>
                    <span className="plays-pricing-pack-price">{copy.playPackPrice(packPrice)}</span>
                  </div>
                  <p className="plays-pricing-pack-contents">
                    {plan.packContentsKey ? copy[plan.packContentsKey] : ''}
                    {plan.maxPacks === null
                      ? ` · ${copy.playPackUnlimited}`
                      : (plan.maxPacks > 0 ? ` · ${copy.playPackMax(plan.maxPacks)}` : '')}
                  </p>
                  <div className="plays-pricing-pack-stepper">
                    <button
                      type="button"
                      onClick={() => setPacks(plan.tier, packCount - 1, plan.maxPacks)}
                      disabled={packCount <= 0}
                      aria-label="Decrease Play Packs"
                    >
                      -
                    </button>
                    <span>{packCount}</span>
                    <button
                      type="button"
                      onClick={() => setPacks(plan.tier, packCount + 1, plan.maxPacks)}
                      disabled={plan.maxPacks !== null && packCount >= (plan.maxPacks || 0)}
                      aria-label="Increase Play Packs"
                    >
                      +
                    </button>
                  </div>
                </div>
              ) : (
                isFree && <div className="plays-pricing-pack plays-pricing-pack-disabled">{copy.playPackNotAvailable}</div>
              )}

              {(() => {
                // On the current paid plan the CTA becomes an "Update packs"
                // action, enabled only when the selected pack count differs
                // from what the merchant already has. The Free card becomes a
                // "Downgrade to Free" action (cancel subscription) when the
                // merchant is currently on a paid plan.
                const packsChanged = isCurrent && !isFree && packCount !== currentPlanPacks;
                const canDowngrade = isFree && !isCurrent;
                const disabled = isPending || (isFree ? !canDowngrade : (isCurrent && !packsChanged));
                let label;
                if (isPending) label = copy.processing;
                else if (packsChanged) label = copy.updatePacks;
                else if (canDowngrade) label = copy.downgradeToFree;
                else if (isCurrent) label = copy.currentPlan;
                else if (isFree) label = copy.freePlan;
                else label = copy.selectPlan;
                const onClick = canDowngrade ? handleDowngradeToFree : () => handleSubscribe(plan);
                return (
                  <button
                    type="button"
                    className="plays-pricing-cta"
                    onClick={onClick}
                    disabled={disabled}
                  >
                    {label}
                  </button>
                );
              })()}
            </div>
          );
        })}
      </div>

      <div className="plays-pricing-footnote" role="note">
        <FootnoteWithContact text={pricingFootnote} />
      </div>

      <div className="plays-pricing-klaviyo">{copy.klaviyoObjection}</div>

      <div className="plays-pricing-multistore">
        <span>{copy.multiStore}</span>
        <a href={CONTACT_MAILTO} className="plays-pricing-multistore-link">
          {copy.talkToUs}
        </a>
      </div>

      {gmvCheckoutNotice && (
        <div className="plays-gmv-overlay" role="dialog" aria-modal="true">
          <div className="plays-gmv-card">
            <p className="plays-gmv-text">
              <FootnoteWithContact text={gmvCheckoutNotice.footnote} />
            </p>
            <button
              type="button"
              className="plays-gmv-continue"
              onClick={() => { window.location.href = gmvCheckoutNotice.confirmationUrl; }}
            >
              {copy.gmvCheckoutContinue}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
